import React, { useState } from "react";
import { Quote, Star, BadgeCheck, PenLine, Phone, Mail, MessageCircle, MapPin, Send, Loader2, CheckCircle2, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "../../context/SiteContext";
import { api, imgUrl, links } from "../../lib/api";
import { Link } from "react-router-dom";
import { Container, Reveal, SectionHead, ContactPills } from "../site/Reveal";
import { ContactButtons } from "../site/ContactButtons";
import { visibleReviews } from "./LocalReviews";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";

const RatingSummary = ({ list, t }) => {
  if (!list.length) return null;
  const avg = list.reduce((a, x) => a + (Number(x.rating) || 5), 0) / list.length;
  const verified = list.filter((x) => x.verified).length;
  return (
    <div className="mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[15px] text-[#4b5d5a]" data-testid="rating-summary">
      <span className="font-jakarta text-[26px] font-extrabold text-[#0f2e2a]" data-testid="rating-average">{avg.toFixed(1)}</span>
      <span className="flex text-[#f59e0b]">{[...Array(5)].map((_, i) => <Star key={i} size={18} fill={i < Math.round(avg) ? "currentColor" : "none"} />)}</span>
      <span data-testid="rating-count">· {list.length} {t("reviews")}</span>
      {verified > 0 && <span className="inline-flex items-center gap-1 font-semibold text-[#0e8c7f]" data-testid="rating-verified"><BadgeCheck size={15} />{verified} {t("verified")}</span>}
    </div>
  );
};

export const Testimonials = () => {
  const { content, t: tr } = useSite();
  const list = visibleReviews(content);
  return (
    <section id="testimonials" className="scroll-mt-28 py-20 md:py-24" data-testid="testimonials-section">
      <Container>
        <SectionHead eyebrow={tr("Testimonials")} title={tr("Customer Reviews")} />
        <RatingSummary list={list} t={tr} />
        {!list.length && <p className="mt-6 text-center text-[15.5px] text-[#4b5d5a]" data-testid="reviews-empty">{tr("Verified reviews from our customers will appear here soon.")}</p>}
        <div className="mt-6 flex justify-center"><Link to="/leave-review" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-1.5 rounded-full border border-[#cfe7e2] bg-white px-4 py-2 text-[14px] font-semibold text-[#0e8c7f] transition-colors hover:bg-[#eef8f6]" data-testid="leave-review-link"><PenLine size={15} />{tr("Leave a review")}</Link></div>
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {list.map((t, idx) => (
            <Reveal key={idx} delay={(idx % 2) * 120} className="sw-card relative p-7 md:p-8">
              <Quote size={36} className="absolute right-7 top-7 text-[#d5f3ed]" />
              <div className="flex gap-0.5 text-[#f59e0b]">{[...Array(5)].map((_, i) => <Star key={i} size={16} fill={i < (Number(t.rating) || 5) ? "currentColor" : "none"} />)}</div>
              <p className="mt-4 text-[16.5px] leading-relaxed text-[#2f4441]">“{t.text}”</p>
              <div className="mt-6 flex items-center gap-3">
                <img src={imgUrl(t.image)} alt={t.name} className="h-12 w-12 rounded-full object-cover ring-2 ring-[#d5f3ed]" loading="lazy" />
                <div>
                  <div className="font-semibold text-[#0f2e2a]">{t.name}</div>
                  <div className="text-[13.5px] text-[#6b7c79]">{t.location}</div>
                  {t.verified && <div className="mt-0.5 inline-flex items-center gap-1 text-[12px] font-semibold text-[#0e8c7f]" data-testid="verified-badge"><BadgeCheck size={13} />{tr("Verified customer")}</div>}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
};

export const About = () => {
  const { content, t } = useSite();
  const a = content?.about || {};
  return (
    <section id="about" className="scroll-mt-28 bg-[#f4fbf9] py-20 md:py-24" data-testid="about-section">
      <Container className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
        <Reveal>
          <div className="sw-section-eyebrow">{t("About Us")}</div>
          <h2 className="sw-h2 mt-3">{a.title}</h2>
          <p className="mt-4 text-[17px] leading-relaxed text-[#4b5d5a]">{a.subtitle}</p>
          <ContactPills center={false} />
          <div className="mt-8 grid grid-cols-2 gap-4">
            {(a.stats || []).map((s) => (
              <div key={s.label} className="rounded-2xl border border-[#e0f0ec] bg-white p-5">
                <div className="font-jakarta text-[24px] font-extrabold text-[#0e8c7f]">{s.value}</div>
                <div className="mt-1 text-[13.5px] text-[#6b7c79]">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-2xl bg-[#0f2e2a] p-6 text-[17px] font-medium leading-relaxed text-white">
            <Quote size={22} className="mb-2 text-[#5fd4c1]" />
            {a.quote}
          </div>
        </Reveal>
        <Reveal delay={120} className="space-y-5 text-[16.5px] leading-[1.75] text-[#3d504d]">
          {(a.paragraphs || []).map((p, i) => <p key={i}>{p}</p>)}
          <ContactButtons id="about" className="pt-2" />
        </Reveal>
      </Container>
    </section>
  );
};

export const FAQ = () => {
  const { content, openChat, t } = useSite();
  return (
    <section id="faqs" className="scroll-mt-28 py-20 md:py-24" data-testid="faq-section">
      <Container className="max-w-[920px] lg:w-[70%]">
        <SectionHead eyebrow={t("FAQs")} title={t("Frequently Asked Questions")} text={t("Answers to the questions people ask us most. Please only send sealed, unexpired items in good condition. We can't accept or return items that are expired, opened or damaged.")} />
        <Reveal className="mt-12">
          <Accordion type="single" collapsible className="space-y-3">
            {(content?.faqs || []).map((f, i) => (
              <AccordionItem key={i} value={`f${i}`} className="rounded-2xl border border-[#e3f0ed] bg-white px-6 data-[state=open]:border-[#9fded2] data-[state=open]:shadow-[0_12px_30px_-18px_rgba(15,46,42,.3)]" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="py-5 text-left font-jakarta text-[17px] font-semibold text-[#0f2e2a] hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="pb-5 text-[15.5px] leading-relaxed text-[#4b5d5a]">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
        <Reveal className="mt-10 flex flex-col items-center gap-4 rounded-3xl bg-[#e9f8f4] p-8 text-center">
          <h3 className="text-[22px] font-bold text-[#0f2e2a]">{t("Still have questions?")}</h3>
          <p className="text-[15.5px] text-[#4b5d5a]">{t("Our team is here to help with anything about selling your diabetic supplies.")}</p>
          <ContactPills className="" />
          <button onClick={() => openChat()} className="sw-btn-primary" data-testid="faq-chat-btn"><MessageCircle size={16} /> {t("Chat With Us")}</button>
        </Reveal>
      </Container>
    </section>
  );
};

export const Contact = () => {
  const { content, openChat, t, lang } = useSite();
  const l = links(content);
  const [form, setForm] = useState({ name: "", email: "", phone: "", items: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || (!form.email.trim() && !form.phone.trim())) {
      toast.error(t("Please add your name and an email or phone number."));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/quotes", { ...form, lang });
      setDone(data.code || "sent");
      setForm({ name: "", email: "", phone: "", items: "", message: "" });
      toast.success(t("Request sent! We'll reach out with your offer shortly."));
    } catch {
      toast.error(t("Something went wrong. Please try again or chat with us."));
    } finally {
      setBusy(false);
    }
  };
  const input = "w-full rounded-xl border border-[#dcebe8] bg-white px-4 py-3 text-[15px] outline-none transition-colors focus:border-[#0e8c7f] focus:ring-2 focus:ring-[#0e8c7f]/15";
  const cards = [
    [Phone, "Call / SMS", content?.phone_display, l.tel],
    [Mail, "Email", content?.email, l.mail],
    ...(content?.email2 ? [[Mail, "Gmail", content.email2, l.mail2]] : []),
    [MessageCircle, "WhatsApp", content?.whatsapp_display, l.wa],
  ];
  return (
    <section id="contact" className="scroll-mt-28 bg-[#f4fbf9] py-20 md:py-24" data-testid="contact-section">
      <Container>
        <SectionHead eyebrow={t("Get in Touch")} title={t("Contact {brand}", { brand: content?.brand })} text={t("Reach us on any channel that works for you.")} />
        <div className="mt-14 grid gap-8 lg:grid-cols-[0.85fr_1.15fr]">
          <Reveal className="space-y-4">
            {cards.map(([I, label, v, href]) => (
              <a key={label} href={href} target={label === "WhatsApp" ? "_blank" : undefined} rel="noreferrer" className="sw-card flex items-center gap-4 p-5" data-testid={`contact-${label.split(" ")[0].toLowerCase()}`}>
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#0e8c7f] text-white"><I size={20} /></span>
                <div className="min-w-0">
                  <div className="text-[12.5px] font-semibold uppercase tracking-wider text-[#6b7c79]">{t(label)}</div>
                  <div className="truncate text-[17px] font-semibold text-[#0f2e2a]">{v}</div>
                </div>
              </a>
            ))}
            <div className="sw-card flex items-center gap-4 p-5">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#d3f6ee] text-[#0e8c7f]"><MapPin size={20} /></span>
              <div>
                <div className="text-[12.5px] font-semibold uppercase tracking-wider text-[#6b7c79]">{t("Address")}</div>
                <div className="text-[17px] font-semibold text-[#0f2e2a]">{content?.address}</div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={120} className="rounded-[26px] border border-[#e0f0ec] bg-white p-7 md:p-9 shadow-[0_30px_60px_-40px_rgba(15,46,42,.35)]">
            <h3 className="text-[24px] font-bold text-[#0f2e2a]">{t("Request a Free Quote")}</h3>
            <p className="mt-1.5 text-[15.5px] text-[#5b6d6a]">{t("Tell us what you have and we'll send a fair offer.")}</p>
            {done && (
              <div className="mt-5 flex items-center gap-2 rounded-xl bg-[#e6f8f3] px-4 py-3 text-[14.5px] font-medium text-[#0b6f65]" data-testid="quote-success">
                <CheckCircle2 size={18} className="shrink-0" />
                <span>{t("Thanks! Your quote number is")} <b data-testid="quote-code">{done}</b>. {t("Save it to")} <Link to={`/track?code=${done}`} className="underline">{t("track your payout")}</Link>.</span>
              </div>
            )}
            <form onSubmit={submit} className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="quote-form">
              <input className={input} placeholder={t("Full name *")} value={form.name} onChange={set("name")} data-testid="quote-name" />
              <input className={input} placeholder={t("Phone")} value={form.phone} onChange={set("phone")} data-testid="quote-phone" />
              <input className={`${input} sm:col-span-2`} type="email" placeholder={t("Email")} value={form.email} onChange={set("email")} data-testid="quote-email" />
              <input className={`${input} sm:col-span-2`} placeholder={t("Supplies (e.g. 3 Dexcom G7, exp. 2027)")} value={form.items} onChange={set("items")} data-testid="quote-items" />
              <textarea className={`${input} sm:col-span-2 min-h-[110px] resize-y`} placeholder={t("Anything else we should know?")} value={form.message} onChange={set("message")} data-testid="quote-message" />
              <div className="flex flex-wrap items-center gap-4 sm:col-span-2">
                <button type="submit" disabled={busy} className="sw-btn-primary disabled:opacity-60" data-testid="quote-submit">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {t("Send Request")}
                </button>
                <span className="text-[14px] text-[#5b6d6a]">{t("Prefer to chat?")} <button type="button" onClick={() => openChat()} className="font-semibold text-[#0e8c7f] hover:underline">{t("Send us a message")}</button></span>
                <span className="w-full text-[14px] text-[#5b6d6a]">{t("Or email us at")} <a href={l.mail} className="break-all font-semibold text-[#0e8c7f] hover:underline" data-testid="quote-email-link">{content?.email}</a>{content?.email2 && <> {t("or")} <a href={l.mail2} className="break-all font-semibold text-[#0e8c7f] hover:underline" data-testid="quote-email2-link">{content.email2}</a></>}</span>
              </div>
            </form>
            <div className="mt-6 flex flex-wrap gap-2 border-t border-[#eef5f3] pt-5">
              <button onClick={() => openChat()} className="sw-chip"><MessageCircle size={14} />{t("Chat with us")}</button>
              <a href={l.tel} className="sw-chip"><Phone size={14} />{t("Call")}</a>
              <a href={l.sms} className="sw-chip"><MessageSquareText size={14} />{t("Text")}</a>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
};
